import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/interactives/Tooltip'
import {
  faChevronRight,
  faClose,
  faGear,
  faListOl,
  faPause,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { cn } from '@/utils'
import { doc, type DocumentData, updateDoc } from 'firebase/firestore'
import { useAtom, useAtomValue, useStore } from 'jotai'
import _find from 'lodash/find'

import { ENVIRONMENT } from '@/shared/environment'
import { arrayIncludes } from '@/utils/primitives'

import { PROMPTER_COLLECTION } from '~shared/consts'
import { CueType } from '~shared/rundown/cue'
import type { Cue } from '~shared/rundown/cue'
import { getContentForRichtextCell } from '~shared/types/Cell.ts'

import { RichtextPreviewRenderer } from '@/features/rundown/cell/cell-types/richtext/RichtextPreviewRenderer.tsx'
import { makeCellKey } from '@/features/rundown/cell/cell.schema.ts'
import { getCell, useCellSnapshot } from '@/features/rundown/data/cells.store'
import { useRundown } from '@/features/rundown/rundown.derived'

import { db } from '../../firebase'
import {
  isScrollingAtom,
  storedAutoScrollToActiveCueAtom,
  storedIndicatorPosAtom,
  storedInvertColorsAtom,
  storedMarginSizeAtom,
  storedShowCueDetailsAtom,
  storedShowOverscrollBlackoutAtom,
  storedTextAlignAtom,
  syncChangesAtom,
  storedTextIsUppercaseAtom,
  storedTextSizeAtom,
  storedSpeedAtom,
} from '../../store/output.store'
import { getNowAtom, runnerAtom } from '../../store/runner.store'
import { CellContext } from '../rundown/cell/cell.context.tsx'
import { useColumnsDerived } from '../rundown/column/column.derived'
import { useCuesDerived } from '../rundown/cue/cue.derived'
import { useCuesById } from '../rundown/data/cues.store'

import * as OUTPUT_TYPES from './rundown-output.const.ts'
import { RundownOutputControlButton } from './RundownOutputControlButton.tsx'
import { usePrompterListener } from './usePrompterListener.ts'
import usePrompterScroll, {
  MAX_SPEED,
  MIN_SPEED,
} from './usePrompterScroll.tsx'

type CustomCSSProperties = React.CSSProperties & {
  [key: `--${string}`]: string | number
}

export type FirestorePrompterUpdateObject = {
  updatedBy?: number
  updatedAt?: number
  isScrolling?: boolean
  speed?: number
  showCueDetails?: boolean
  showOverscrollBlackout?: boolean
  autoScrollToActiveCue?: boolean
  textIsUppercase?: boolean
  invertColors?: boolean
  jumpToActiveCue?: boolean
  resetScroll?: boolean
  indicatorPos?: number
  marginSize?: number
  textSize?: number
  textAlign?: string
  scrollToCue?: string
}

// Random number to assign to the specific user - This ensures that updates coming from the DB are from other users
const sessionId = Math.floor(Math.random() * 10000000)

export function RundownOutputLayoutPrompter({
  controlsVisible,
}: {
  controlsVisible: boolean
}) {
  const rundown = useRundown()
  const runner = useAtomValue(runnerAtom)
  const cues = useCuesById()
  const store = useStore()

  const prompterId = rundown.prompterId

  const getNow = useAtomValue(getNowAtom)
  const [syncChanges, setSyncChanges] = useAtom(syncChangesAtom)
  const [marginSize, setMarginSize] = useAtom(storedMarginSizeAtom)
  const [textSize, setTextSize] = useAtom(storedTextSizeAtom)
  const [indicatorPos, setIndicatorPos] = useAtom(storedIndicatorPosAtom)
  const [showCueDetails, setShowCueDetails] = useAtom(storedShowCueDetailsAtom)
  const [showOverscrollBlackout, setShowOverscrollBlackout] = useAtom(
    storedShowOverscrollBlackoutAtom,
  )
  const [textIsUppercase, setTextIsUppercase] = useAtom(
    storedTextIsUppercaseAtom,
  )
  const [invertColors, setInvertColors] = useAtom(storedInvertColorsAtom)
  const [textAlign, setTextAlign] = useAtom(storedTextAlignAtom)
  const [autoScrollToActiveCue, setAutoScrollToActiveCue] = useAtom(
    storedAutoScrollToActiveCueAtom,
  )
  const isScrolling = useAtomValue(isScrollingAtom)
  const [showPrompterControls, setShowPrompterControls] = useState(false)
  const [showCuesList, setShowCuesList] = useState(false)

  const currentCueId = useRef<string | null>(null)
  const syncChangesRef = useRef<boolean>(false)

  // syncNumberStore will keep track of the highest number stored in the DB and only update the local instance when it is behind.
  // This is useful for actions that require the prompter to scroll to specific places based on another users push of a button
  const updatedAtRef = useRef<number>(getNow())

  const {
    containerRef,
    toggleScrolling,
    startScrolling,
    stopScrolling,
    resetScroll,
    setCurrentSpeed,
    jumpToActiveCue,
    scrollToCue,
  } = usePrompterScroll()

  const { indexedCuesWithoutSkipped: cueIndexList } = useCuesDerived()

  const updateFirestore = useCallback(
    async (update: FirestorePrompterUpdateObject) => {
      if (!prompterId) return
      const docRef = doc(db, PROMPTER_COLLECTION, prompterId)
      return updateDoc(docRef, {
        jumpToActiveCue: false,
        resetScroll: false,
        scrollToCue: null,
        updatedBy: sessionId,
        updatedAt: getNow(),
        ...update,
      })
    },
    [getNow, prompterId],
  )

  const functionWrapperMap = useMemo(() => {
    return {
      isScrolling: { handler: toggleScrolling },
      speed: { handler: setCurrentSpeed },
      resetScroll: { handler: resetScroll },
      jumpToActiveCue: { handler: jumpToActiveCue },
      textIsUppercase: { handler: setTextIsUppercase },
      invertColors: { handler: setInvertColors },
      scrollToCue: { handler: scrollToCue },
      indicatorPos: { handler: setIndicatorPos },
      marginSize: { handler: setMarginSize },
      textSize: { handler: setTextSize },
      showCueDetails: { handler: setShowCueDetails },
      showOverscrollBlackout: { handler: setShowOverscrollBlackout },
      autoScrollToActiveCue: { handler: setAutoScrollToActiveCue },
      textAlign: { handler: setTextAlign },
    } as const
  }, [
    jumpToActiveCue,
    resetScroll,
    scrollToCue,
    setAutoScrollToActiveCue,
    setCurrentSpeed,
    setIndicatorPos,
    setInvertColors,
    setMarginSize,
    setShowCueDetails,
    setShowOverscrollBlackout,
    setTextAlign,
    setTextIsUppercase,
    setTextSize,
    toggleScrolling,
  ])

  const updateFirestoreWrapper = useCallback(
    (update: FirestorePrompterUpdateObject) => {
      if (syncChanges) void updateFirestore(update)

      return (
        Object.keys(update) as Array<keyof typeof functionWrapperMap>
      ).map((key) => {
        return functionWrapperMap[key].handler(update[key] as never)
      })
    },
    [functionWrapperMap, syncChanges, updateFirestore],
  )

  // Listener function that listens to the DB and updates the local version if sync is enabled
  const handlePrompterUpdate = useCallback(
    (doc: DocumentData) => {
      if (!syncChangesRef.current) return
      const docData = doc.data() as FirestorePrompterUpdateObject
      if (docData.updatedBy === sessionId) return
      if (!docData.updatedAt || docData.updatedAt < updatedAtRef.current) return

      // Start/Stop changes
      if (docData.isScrolling === true) {
        startScrolling()
      } else if (docData.isScrolling === false) {
        stopScrolling()
      }

      // Speed
      if (typeof docData.speed === 'number') {
        setCurrentSpeed(docData.speed)
      }

      // Indicator position
      if (typeof docData.indicatorPos === 'number') {
        setIndicatorPos(docData.indicatorPos)
      }

      // Margin size
      if (typeof docData.marginSize === 'number') {
        setMarginSize(docData.marginSize)
      }

      // Text size
      if (typeof docData.textSize === 'number') {
        setTextSize(docData.textSize)
      }

      // Show/Hide cue details
      if ('showCueDetails' in docData) {
        setShowCueDetails(docData.showCueDetails!)
      }

      // Show/Hide scroll blackout
      if ('showOverscrollBlackout' in docData) {
        setShowOverscrollBlackout(docData.showOverscrollBlackout!)
      }

      // Autoscroll to active cue
      if ('autoScrollToActiveCue' in docData) {
        setAutoScrollToActiveCue(docData.autoScrollToActiveCue!)
      }

      // Uppercase text
      if ('textIsUppercase' in docData) {
        setTextIsUppercase(docData.textIsUppercase!)
      }

      // Invert colors
      if ('invertColors' in docData) {
        setInvertColors(docData.invertColors!)
      }

      // Text alignment
      if (typeof docData.textAlign === 'string') {
        setTextAlign(docData.textAlign)
      }

      // Jump to active cue
      if (docData.jumpToActiveCue) {
        jumpToActiveCue()
      }

      // Reset scroll
      if (docData.resetScroll) {
        resetScroll()
      }

      // Scroll to cue
      if (typeof docData.scrollToCue === 'string') {
        scrollToCue(docData.scrollToCue)
      }
    },
    [
      startScrolling,
      stopScrolling,
      setCurrentSpeed,
      setIndicatorPos,
      setMarginSize,
      setTextSize,
      setShowCueDetails,
      setShowOverscrollBlackout,
      setAutoScrollToActiveCue,
      setTextIsUppercase,
      setInvertColors,
      setTextAlign,
      jumpToActiveCue,
      resetScroll,
      scrollToCue,
    ],
  )

  usePrompterListener(handlePrompterUpdate)

  /**
   * Handle Keyboard shortcuts
   */
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      switch (event.code) {
        /**
         * Start/stop scrolling
         */
        case 'Space':
          event.preventDefault()
          updateFirestoreWrapper({ isScrolling: !isScrolling })
          break
        /**
         * Decrease speed
         */
        case 'ArrowLeft': {
          event.preventDefault()
          const currentSpeed = store.get(storedSpeedAtom)
          if (event.shiftKey) {
            const amount = currentSpeed - 3
            updateFirestoreWrapper({ speed: amount })
          } else {
            const amount = currentSpeed - 1
            updateFirestoreWrapper({ speed: amount })
          }
          break
        }
        /**
         * Increase speed
         */
        case 'ArrowRight': {
          event.preventDefault()
          const currentSpeed = store.get(storedSpeedAtom)
          if (event.shiftKey) {
            const amount = currentSpeed + 3
            updateFirestoreWrapper({ speed: amount })
          } else {
            const amount = currentSpeed + 1
            updateFirestoreWrapper({ speed: amount })
          }
          break
        }
        /**
         * Jump to top
         */
        case 'ArrowUp':
          if (event.shiftKey) {
            event.preventDefault()
            updateFirestoreWrapper({ resetScroll: true })
          }
          break
        /**
         * Jump to active cue
         */
        case 'ArrowDown':
          if (event.shiftKey) {
            event.preventDefault()
            updateFirestoreWrapper({ jumpToActiveCue: true })
          }
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [
    indicatorPos,
    isScrolling,
    jumpToActiveCue,
    resetScroll,
    setCurrentSpeed,
    store,
    toggleScrolling,
    updateFirestoreWrapper,
  ])

  const currentCue = runner?.timesnap?.cueId
  /**
   * Scroll to active cue when runner.timesnap.cueId changes
   */
  useEffect(() => {
    if (currentCue !== currentCueId.current && autoScrollToActiveCue) {
      jumpToActiveCue()
      currentCueId.current = currentCue
    }
  }, [autoScrollToActiveCue, currentCue, indicatorPos, jumpToActiveCue])

  return (
    <div className="relative h-full">
      {/* Stop/Start button  */}
      <div
        className="absolute left-4 z-10"
        style={{
          marginLeft: `${marginSize}vw`,
          top: `${indicatorPos}%`,
        }}
      >
        <button
          className={cn([
            'py-2 px-3 rounded w-16 h-20',
            invertColors ? 'text-black' : 'text-white',
          ])}
          onClick={() => updateFirestoreWrapper({ isScrolling: !isScrolling })}
        >
          <FontAwesomeIcon
            icon={isScrolling ? faChevronRight : faPause}
            size="4x"
          />
        </button>
        {syncChanges && (
          <p className="text-[10px] text-center mt-1 text-blue-500 uppercase font-semibold rounded-sm">
            SYNCED
          </p>
        )}
        <p className="text-[10px] text-center mt-1 text-gray-600 uppercase font-semibold">
          Speed: <SpeedValue />
        </p>
      </div>

      {/* Top Overscroll Blackout with text is scrolled past indicator */}
      {showOverscrollBlackout && (
        <div
          className={cn([
            'w-full h-full absolute left-0 right-0 z-10 pointer-events-none',
            invertColors ? 'bg-white/80' : 'bg-black/80',
          ])}
          style={{
            top: `${indicatorPos - 105}%`,
          }}
        ></div>
      )}

      {controlsVisible && (
        <div
          data-keep-controls-while-active // Ensures that while the user is focused on an element within this parent we do not hide the controls.
          className="flex flex-col items-end gap-1 fixed bottom-14 right-3 z-10"
        >
          {showPrompterControls ? (
            <div className="px-2 py-3 flex flex-col gap-1 rounded bg-gray-800/90">
              <button
                className="flex items-baseline justify-between gap-1 text-sm"
                onClick={() =>
                  updateFirestoreWrapper({ isScrolling: !isScrolling })
                }
              >
                Start/Stop
                <span className="text-[10px] font-semibold bg-gray-700 px-1 py-0.5 rounded-sm">
                  SPACE
                </span>
              </button>
              <button
                className="flex items-baseline justify-between gap-1 text-sm"
                onClick={() => updateFirestoreWrapper({ resetScroll: true })}
              >
                Jump to start
                <span className="text-[10px] font-semibold bg-gray-700 px-1 py-0.5 rounded-sm">
                  SHIFT ↑
                </span>
              </button>
              <button
                className="flex items-baseline justify-between gap-1 text-sm"
                onClick={() =>
                  updateFirestoreWrapper({ jumpToActiveCue: true })
                }
              >
                Jump to active cue
                <span className="text-[10px] font-semibold bg-gray-700 px-1 py-0.5 rounded-sm">
                  SHIFT ↓
                </span>
              </button>
              <div className="flex items-baseline justify-between gap-1 text-sm">
                Adjust speed
                <p className="space-x-1">
                  <span className="text-[10px] font-semibold bg-gray-700 px-1 py-0.5 rounded-sm">
                    ←
                  </span>
                  <span className="text-[10px] font-semibold bg-gray-700 px-1 py-0.5 rounded-sm">
                    →
                  </span>
                </p>
              </div>
              <span className="my-2 border-b border-white/10"></span>
              <label className="flex gap-2 items-baseline justify-between">
                <p className="w-2/3 inline-block text-sm">Speed</p>
                <SpeedInput
                  onChange={(speed) => updateFirestoreWrapper({ speed })}
                />
              </label>
              <label className="flex gap-2 items-baseline justify-between">
                <p className="w-2/3 inline-block text-sm">Indicator</p>
                <input
                  type="number"
                  className="w-1/3 font-mono rounded-sm"
                  min={1}
                  max={90}
                  step={1}
                  value={indicatorPos}
                  onChange={(e) =>
                    updateFirestoreWrapper({
                      indicatorPos: Number(e.target.value),
                    })
                  } // Update local state only
                />
              </label>
              <label className="flex gap-2 items-baseline justify-between">
                <p className="w-2/3 inline-block text-sm">Margin</p>
                <input
                  type="number"
                  className="w-1/3 font-mono rounded-sm"
                  min={0}
                  max={40}
                  step={1}
                  value={marginSize}
                  onChange={(e) =>
                    updateFirestoreWrapper({
                      marginSize: Number(e.target.value),
                    })
                  } // Update local state only
                />
              </label>
              <label className="flex gap-2 items-baseline justify-between">
                <p className="w-2/3 inline-block text-sm">Text size</p>
                <input
                  type="number"
                  className="w-1/3 font-mono rounded-sm"
                  min={10}
                  max={100}
                  step={1}
                  value={textSize}
                  onChange={(e) =>
                    updateFirestoreWrapper({ textSize: Number(e.target.value) })
                  } // Update local state only
                />
              </label>
              <label className="flex gap-2 items-baseline justify-between">
                <p className="w-2/3 inline-block text-sm">Text align</p>
                <select
                  className="w-1/3 font-mono rounded-sm bg-gray-700 text-white text-sm px-1 py-0.5 outline-none focus:ring-3"
                  value={textAlign}
                  onChange={(e) =>
                    updateFirestoreWrapper({ textAlign: e.target.value })
                  }
                >
                  <option value="start">Auto</option>
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                  <option value="justify">Justify</option>
                </select>
              </label>
              <span className="my-2 border-b border-white/10"></span>
              <label
                htmlFor="showCueDetails"
                className={cn(
                  'px-2 py-1 bg-gray-800 rounded-sm flex items-center gap-2 hover:brightness-110',
                )}
              >
                <input
                  id="showCueDetails"
                  type="checkbox"
                  className="focus:outline-hidden focus:ring-3"
                  checked={showCueDetails}
                  onChange={(e) =>
                    updateFirestoreWrapper({ showCueDetails: e.target.checked })
                  }
                />
                <span>Show cue details</span>
              </label>
              <label
                htmlFor="showOverscrollBlackout"
                className={cn(
                  'px-2 py-1 bg-gray-800 rounded-sm flex items-center gap-2 hover:brightness-110',
                )}
              >
                <input
                  id="showOverscrollBlackout"
                  type="checkbox"
                  className="focus:outline-hidden focus:ring-3"
                  checked={showOverscrollBlackout}
                  onChange={(e) =>
                    updateFirestoreWrapper({
                      showOverscrollBlackout: e.target.checked,
                    })
                  }
                />
                <span>Show scroll blackout</span>
              </label>
              <label
                htmlFor="autoScrollToActive"
                className="px-2 py-1 bg-gray-800 rounded-sm flex gap-2 hover:brightness-110"
              >
                <input
                  id="autoScrollToActive"
                  type="checkbox"
                  className="focus:outline-hidden focus:ring-3"
                  checked={autoScrollToActiveCue}
                  onChange={(e) =>
                    updateFirestoreWrapper({
                      autoScrollToActiveCue: e.target.checked,
                    })
                  }
                />
                <span>Auto-scroll to active cue</span>
              </label>
              <label
                htmlFor="textIsUppercase"
                className={cn(
                  'px-2 py-1 bg-gray-800 rounded-sm flex items-center gap-2 hover:brightness-110',
                )}
              >
                <input
                  id="textIsUppercase"
                  type="checkbox"
                  className="focus:outline-hidden focus:ring-3"
                  checked={textIsUppercase}
                  onChange={(e) =>
                    updateFirestoreWrapper({
                      textIsUppercase: e.target.checked,
                    })
                  }
                />
                <span>Uppercase text</span>
              </label>
              <label
                htmlFor="invertColors"
                className={cn(
                  'px-2 py-1 bg-gray-800 rounded-sm flex items-center gap-2 hover:brightness-110',
                )}
              >
                <input
                  id="invertColors"
                  type="checkbox"
                  className="focus:outline-hidden focus:ring-3"
                  checked={invertColors}
                  onChange={(e) =>
                    updateFirestoreWrapper({ invertColors: e.target.checked })
                  }
                />
                <span>Invert colors</span>
              </label>
              <span className="my-2 border-b border-white/10"></span>
              <Tooltip>
                <TooltipContent>
                  Sync settings, controls and progress with all other prompter
                  viewers
                </TooltipContent>
                <TooltipTrigger>
                  <label
                    htmlFor="syncChanges"
                    className={cn(
                      'px-2 py-1 bg-gray-800 rounded-sm flex items-center gap-2 hover:brightness-110',
                    )}
                  >
                    <input
                      id="syncChanges"
                      type="checkbox"
                      className="focus:outline-hidden focus:ring-3"
                      checked={syncChanges}
                      onChange={(e) => {
                        setSyncChanges(e.target.checked)
                        syncChangesRef.current = e.target.checked
                      }}
                    />
                    <span>Sync with others</span>
                  </label>
                </TooltipTrigger>
              </Tooltip>
            </div>
          ) : (
            ''
          )}
          {showCuesList ? (
            <div className="px-2 py-3 flex flex-col items-start gap-2 rounded bg-gray-800/90 max-h-125 overflow-auto ">
              {cueIndexList.map((cueIndex, i) => {
                const cue = cues[cueIndex.id] as Cue
                return (
                  <button
                    key={i}
                    id={cue.id}
                    disabled={arrayIncludes(
                      [CueType.GROUP, CueType.HEADING],
                      cue.type,
                    )}
                    className={cn([
                      'flex justify-stretch items-center gap-1 text-left w-full rounded-sm disabled:pointer-events-none',
                    ])}
                    onClick={() =>
                      updateFirestoreWrapper({ scrollToCue: cue.id })
                    }
                  >
                    <p
                      className={cn(
                        'text-[10px] min-w-6 px-1 text-center font-mono font-semibold rounded-xs self-stretch flex items-center justify-center',
                        currentCue === cueIndex.id
                          ? 'bg-red-500'
                          : 'bg-[#171717]',
                        arrayIncludes([CueType.HEADING], cue.type)
                          ? 'invisible'
                          : '',
                      )}
                    >
                      {cueIndex.displayIndex}
                    </p>
                    <div
                      className={cn(
                        'w-48 px-1.5 py-1 hover:brightness-110 transition-colors rounded-xs',
                        arrayIncludes(
                          [CueType.GROUP, CueType.HEADING],
                          cue.type,
                        )
                          ? 'bg-transparent! text-xs'
                          : 'text-sm',
                      )}
                      style={{
                        backgroundColor:
                          cue.backgroundColor === 'transparent'
                            ? '#171717'
                            : cue.backgroundColor || '#171717',
                      }}
                    >
                      <p className="w-full truncate overflow-hidden">
                        {cue.title || 'Cue'}
                      </p>
                      <p className="truncate overflow-hidden text-xs opacity-65">
                        {cue.subtitle}
                      </p>
                    </div>
                  </button>
                )
              })}
            </div>
          ) : (
            ''
          )}
          {/* CONTROLS */}
          <div className="flex gap-1">
            <RundownOutputControlButton
              tooltip="Cues list"
              icon={showCuesList ? faClose : faListOl}
              onClick={() => {
                setShowPrompterControls(false)
                setShowCuesList(!showCuesList)
              }}
            />
            <RundownOutputControlButton
              tooltip="Prompter settings"
              icon={showPrompterControls ? faClose : faGear}
              onClick={() => {
                setShowCuesList(false)
                setShowPrompterControls(!showPrompterControls)
              }}
            />
          </div>
        </div>
      )}
      <div
        ref={containerRef}
        className={cn([
          'h-[90vh] overflow-auto px-25',
          invertColors ? 'invert hue-rotate-180 bg-black' : '',
        ])}
      >
        <div
          style={{
            marginLeft: `${marginSize}vw`,
            marginRight: `${marginSize}vw`,
          }}
        >
          <PrompterContent />
        </div>
      </div>
    </div>
  )
}

// Speed lives in its own components so speed changes only re-render these
function SpeedValue() {
  const speed = useAtomValue(storedSpeedAtom)
  return <span className="tabular-nums">{speed}</span>
}

function SpeedInput({ onChange }: { onChange: (speed: number) => void }) {
  const speed = useAtomValue(storedSpeedAtom)
  return (
    <input
      type="number"
      className="w-1/3 font-mono rounded-sm"
      min={MIN_SPEED}
      max={MAX_SPEED}
      step={1}
      value={speed}
      onChange={(e) => onChange(Number(e.target.value))}
    />
  )
}

// Reads its own state so layout re-renders don't reach the script
const PrompterContent = memo(function PrompterContent() {
  const runner = useAtomValue(runnerAtom)
  const cues = useCuesById()
  const { sortedColumns } = useColumnsDerived()
  const [searchParams] = useSearchParams()
  const textSize = useAtomValue(storedTextSizeAtom)
  const showCueDetails = useAtomValue(storedShowCueDetailsAtom)
  const textIsUppercase = useAtomValue(storedTextIsUppercaseAtom)
  const textAlign = useAtomValue(storedTextAlignAtom)

  const visibleColumnSearchParam = searchParams.get(OUTPUT_TYPES.COLUMN)
  const prompterColumn = useMemo(
    () => _find(sortedColumns, { id: visibleColumnSearchParam }),
    [visibleColumnSearchParam, sortedColumns],
  )

  const currentCue = runner?.timesnap.cueId ? cues[runner.timesnap.cueId] : null
  const nextCue = runner?.nextCueId ? cues[runner?.nextCueId] : null
  const { flatCueIdsWithoutSkipped: cuesOrder } = useCuesDerived()

  // Walk the cue order (not the cell docs) so cues with no cell content still
  // render on the prompter with a blank content area, instead of being skipped
  const cueIdsToRender = useMemo(() => {
    return cuesOrder.filter((cueId) => {
      const cue = cues[cueId]
      return !!cue && !arrayIncludes([CueType.GROUP, CueType.HEADING], cue.type)
    })
  }, [cues, cuesOrder])

  // Partial State: When user did not select a column for the prompter
  if (!prompterColumn)
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <p className="text-xl opacity-70">
          To get started, select a script column in the &quot;Generate
          Output&quot; window.{' '}
          <a
            href={`${ENVIRONMENT.marketingLinks.docs}/sharing/output/#prompter`}
            target="_blank"
            rel="noreferrer"
            className="underline hover:brightness:110"
          >
            See docs for help
          </a>
          .
        </p>
      </div>
    )

  // Ideal State: A column has been selected
  return (
    <div className="relative ProseMirror ProseMirrorOutput h-full pt-[40vh] pb-[100vh]">
      {cueIdsToRender.map((cueId) => {
        const cellKey = makeCellKey(cueId, prompterColumn.id)
        const cell = getCell(cellKey)

        const cue = cues[cueId]

        return (
          <div
            key={cueId}
            id={cueId === currentCue?.id ? 'active-cell' : (cell?.id ?? cueId)}
            data-cue-id={cueId}
            style={
              {
                fontSize: 'var(--output-text-size)',
                '--output-text-size': `${textSize}px`,
                '--output-text-align': textAlign,
              } as CustomCSSProperties
            }
            className={cn([
              'relative mt-4 w-full px-4 pb-4 rounded-sm border-2',
              showCueDetails ? 'border-white/10' : 'border-transparent',
              textIsUppercase ? 'uppercase' : '',
            ])}
          >
            {/* Cue title and background color */}
            {showCueDetails && (
              <p
                className="px-2 py-1 -mx-4 mb-4 bg-gray-100/10 text-white/50 rounded-t-sm"
                style={
                  {
                    backgroundColor:
                      cue?.backgroundColor === 'transparent'
                        ? '#fff 10%'
                        : cue?.backgroundColor || '#fff 10%',
                    fontSize: 'var(--output-text-size)',
                    '--output-text-size': `${textSize}px`,
                  } as CustomCSSProperties
                }
              >
                {cue?.title}
              </p>
            )}

            {/* Current cue indicator */}
            <div
              className={cn([
                'absolute h-full -left-4 top-0 rounded-full',
                cueId === currentCue?.id ? 'border-3 border-red-500' : '', // Active cue
                cueId === nextCue?.id ? 'border-3 border-white' : '', // Next cue
              ])}
            ></div>

            <CellContext.Provider value={cellKey}>
              <CellContent />
            </CellContext.Provider>
          </div>
        )
      })}
    </div>
  )
})

// Memoized so the rich text is only re-parsed when the cell content changes
const CellContent = memo(function CellContent() {
  const cell = useCellSnapshot()

  const contentAsHTML = getContentForRichtextCell(cell?.content)

  return <RichtextPreviewRenderer contentAsHTML={contentAsHTML} />
})
